<template>
  <div
    class="absolute z-10 min-w-130 bg-white px-4 py-2 shadow-md"
    data-cy="app-token-modal"
    :style="positionStyle"
  >
    <div class="flex flex-col space-y-2">
      <tracking-token-inputs @value:change="tokenValues = $event" />
      <utm-parameter-selector @value:change="utmValues = $event" />
      <div class="flex w-full items-center justify-end space-x-3">
        <button
          :class="hasSomeValue ? 'text-primary' : 'text-disabled-medium'"
          data-cy="app-token-save"
          :disabled="!hasSomeValue"
          @click.prevent="saveValues"
        >
          {{ $t("COMMON.SAVE") }}
        </button>
        <button
          class="text-error"
          data-cy="app-token-cancel"
          @click.prevent="emit('value:save', '')"
        >
          {{ $t("COMMON.CANCEL") }}
        </button>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import UtmParameterSelector from "@/components/dynamicFields/UtmParameterSelector.vue";
import TrackingTokenInputs from "@/components/dynamicFields/TrackingTokenInputs.vue";

import { computed, ref } from "vue";
import { appTrackingTokenKey } from "@/helpers/textEditor";
import { UTM_DYNAMIC_PARAMETERS_MAP } from "@/helpers/constants/tracking";
const { position } = defineProps<{
  position: {
    bottom: number;
    left: number;
    right: number;
    top: number;
    height: number;
  };
}>();

const emit = defineEmits<{
  "value:save": [value: string];
}>();

const layoutValues = {
  barHeight: 45,
  leftOffset: 20,
  leftOverlapOffset: 150,
  maxAvailableWidth: 900
} as const;

const tokenValues = ref<{ value: string; id: string }[]>([]);
const utmValues = ref<{ type: string; value: string; id: string }[]>([]);

const hasSomeValue = computed(
  () =>
    !!tokenValues.value.map(({ value }) => !!value).some(Boolean) ||
    utmValues.value.map(({ value }) => !!value).some(Boolean)
);

const positionStyle = computed(() => {
  const topPosition = position.top + position.height + layoutValues.barHeight;
  const leftPosition =
    position.left > layoutValues.maxAvailableWidth
      ? position.left - layoutValues.leftOverlapOffset
      : position.left - layoutValues.leftOffset;
  return {
    top: `${topPosition}px`,
    left: `${leftPosition}px`
  };
});

const saveValues = () => {
  const tokensWithValues = tokenValues.value.reduce(
    (tokens: string[], { value }) => {
      if (!value) {
        return tokens;
      }

      tokens.push(`${appTrackingTokenKey}${value}`);
      return tokens;
    },
    []
  );

  const utmTokensWithValues = utmValues.value.reduce(
    (tokens: string[], { type, value }) => {
      if (!value) {
        return tokens;
      }

      tokens.push(
        `${UTM_DYNAMIC_PARAMETERS_MAP[type as keyof typeof UTM_DYNAMIC_PARAMETERS_MAP]}${value}`
      );
      return tokens;
    },
    []
  );

  const value = `__${[...tokensWithValues, ...utmTokensWithValues].join(",")}`;

  emit("value:save", value);
};
</script>
